/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-NevaiMeet-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, useRef } from "react";
import { Button } from "@vector-im/compound-web";

import { FieldRow, InputField } from "../input/Input";
import { Slider } from "../Slider";
import { useSetting } from "./settings";
import {
  customLivekitUrl,
  matrixRTCMode,
  showConnectionStats,
} from "./settings";
import {
  cameraFrameRate,
  cameraResolution,
  callQuality,
  dataSaver,
  echoCancellation,
  equalizerBands,
  equalizerEnabled,
  joinCameraOff,
  joinMuted,
  liveCaptions,
  noiseGateThreshold,
  noiseSuppression,
  pushToTalk,
  showCallTimer,
  videoBitrateKbps,
  videoFilter,
  voiceChangerPreset,
  virtualBackground,
} from "../meet/MeetSettings";
import styles from "./MeetSettings.module.css";

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const EQ_FREQS = [60, 250, 1000, 4000, 12000];

export const MeetingSettingsTab: FC = () => {
  const [muted, setMuted] = useSetting(joinMuted);
  const [camOff, setCamOff] = useSetting(joinCameraOff);
  const [saver, setSaver] = useSetting(dataSaver);
  const [res, setRes] = useSetting(cameraResolution);
  const [fps, setFps] = useSetting(cameraFrameRate);
  const [echo, setEcho] = useSetting(echoCancellation);
  const [noiseSup, setNoiseSup] = useSetting(noiseSuppression);
  const [eqOn, setEqOn] = useSetting(equalizerEnabled);
  const [bands, setBands] = useSetting(equalizerBands);
  const [voice, setVoice] = useSetting(voiceChangerPreset);
  const [gate, setGate] = useSetting(noiseGateThreshold);
  const [ptt, setPtt] = useSetting(pushToTalk);
  const [filter, setFilter] = useSetting(videoFilter);
  const [bg, setBg] = useSetting(virtualBackground);
  const [captions, setCaptions] = useSetting(liveCaptions);
  const [stats, setStats] = useSetting(showConnectionStats);
  const [rtcMode, setRtcMode] = useSetting(matrixRTCMode);
  const [lkUrl, setLkUrl] = useSetting(customLivekitUrl);
  const [quality, setQuality] = useSetting(callQuality);
  const [bitrate, setBitrate] = useSetting(videoBitrateKbps);
  const [showTimer, setShowTimer] = useSetting(showCallTimer);
  const bgInput = useRef<HTMLInputElement>(null);

  const enablePowerMode = (): void => {
    setQuality("max");
    setSaver(false);
    setRes("2160");
    setFps(60);
    setBitrate(0);
  };

  return (
    <div>
      <div className={styles.powerBox}>
        <div>
          <div className={styles.powerText}>Power &amp; Unlimited Mode</div>
          <div className={styles.powerSub}>
            1080p @ 60fps, up to 8 Mbps, studio stereo audio — no caps.
          </div>
        </div>
        <Button onClick={enablePowerMode}>Enable max quality</Button>
      </div>
      <h3 className={styles.sectionTitle}>Default Join Behavior</h3>
      <FieldRow>
        <InputField
          id="joinMuted"
          type="checkbox"
          label="Join with microphone muted"
          checked={muted}
          onChange={(e) => setMuted(e.target.checked)}
        />
      </FieldRow>
      <FieldRow>
        <InputField
          id="joinCamOff"
          type="checkbox"
          label="Join with camera off"
          checked={camOff}
          onChange={(e) => setCamOff(e.target.checked)}
        />
      </FieldRow>

      <h3 className={styles.sectionTitle}>Bandwidth / Data Saver</h3>
      <FieldRow>
        <InputField
          id="saver"
          type="checkbox"
          label="Data saver (force 360p @ 15fps)"
          checked={saver}
          onChange={(e) => setSaver(e.target.checked)}
        />
      </FieldRow>
      <FieldRow>
        <label htmlFor="res">Camera resolution</label>
        <select
          id="res"
          value={res}
          onChange={(e) => setRes(e.target.value as typeof res)}
        >
          <option value="360">360p</option>
          <option value="720">720p (HD)</option>
          <option value="1080">1080p (Full HD)</option>
          <option value="1440">1440p (2K)</option>
          <option value="2160">2160p (4K UHD)</option>
          <option value="4320">4320p (8K UHD)</option>
          <option value="11264">11K</option>
        </select>
      </FieldRow>
      <FieldRow>
        <label htmlFor="fps">Frame rate</label>
        <select
          id="fps"
          value={String(fps)}
          onChange={(e) => setFps(Number(e.target.value) as typeof fps)}
        >
          <option value="15">15 fps</option>
          <option value="30">30 fps</option>
          <option value="60">60 fps</option>
        </select>
      </FieldRow>

      <h3 className={styles.sectionTitle}>Audio Processing (Web Audio API)</h3>
      <FieldRow>
        <InputField
          id="echo"
          type="checkbox"
          label="Echo cancellation"
          checked={echo}
          onChange={(e) => setEcho(e.target.checked)}
        />
      </FieldRow>
      <FieldRow>
        <InputField
          id="noiseSup"
          type="checkbox"
          label="Noise suppression"
          checked={noiseSup}
          onChange={(e) => setNoiseSup(e.target.checked)}
        />
      </FieldRow>
      <FieldRow>
        <InputField
          id="ptt"
          type="checkbox"
          label="Push-to-talk (hold hotkey to speak)"
          checked={ptt}
          onChange={(e) => setPtt(e.target.checked)}
        />
      </FieldRow>
      <FieldRow>
        <label htmlFor="voice">Voice changer</label>
        <select
          id="voice"
          value={voice}
          onChange={(e) => setVoice(e.target.value as typeof voice)}
        >
          <option value="none">Off</option>
          <option value="deep">Deep voice</option>
          <option value="chipmunk">Chipmunk</option>
          <option value="robot">Robot</option>
        </select>
      </FieldRow>
      <div className={styles.row}>
        <label>Noise gate ({gate} dB)</label>
        <Slider
          label="Noise gate"
          min={-100}
          max={0}
          step={1}
          value={gate}
          onValueChange={setGate}
          onValueCommit={setGate}
        />
      </div>
      <FieldRow>
        <InputField
          id="eqOn"
          type="checkbox"
          label="Enable 5-band equalizer"
          checked={eqOn}
          onChange={(e) => setEqOn(e.target.checked)}
        />
      </FieldRow>
      {eqOn &&
        EQ_FREQS.map((freq, i) => (
          <div key={freq} className={styles.row}>
            <label>{freq} Hz ({bands[i] ?? 0} dB)</label>
            <Slider
              label={`${freq} Hz`}
              min={-12}
              max={12}
              step={1}
              value={bands[i] ?? 0}
              onValueChange={(v) => {
                const next = [...bands];
                next[i] = v;
                setBands(next);
              }}
              onValueCommit={(v) => {
                const next = [...bands];
                next[i] = v;
                setBands(next);
              }}
            />
          </div>
        ))}

      <h3 className={styles.sectionTitle}>Video Filters & Background</h3>
      <FieldRow>
        <label htmlFor="filter">Video filter</label>
        <select
          id="filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
        >
          <option value="none">None</option>
          <option value="brightness">Brightness</option>
          <option value="contrast">Contrast</option>
          <option value="saturate">Saturate</option>
          <option value="sepia">Sepia</option>
          <option value="grayscale">Grayscale</option>
          <option value="invert">Invert</option>
        </select>
      </FieldRow>
      <FieldRow>
        <label>Virtual background</label>
        <span>
          <Button onClick={() => bgInput.current?.click()}>
            {bg ? "Replace" : "Upload image"}
          </Button>
          {bg && (
            <Button onClick={() => setBg("")}>Clear</Button>
          )}
          <input
            ref={bgInput}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (f) setBg(await fileToDataUrl(f));
            }}
          />
        </span>
      </FieldRow>

      <h3 className={styles.sectionTitle}>Call Quality (No Limits)</h3>
      <p className={styles.small}>
        Choose how much bandwidth and quality to use. &ldquo;Maximum&rdquo;
        pushes up to 8K/11K video @ 60fps with a high bitrate and
        studio-quality stereo audio &mdash; no artificial caps.
      </p>
      <FieldRow>
        <label htmlFor="quality">Quality preset</label>
        <select
          id="quality"
          value={quality}
          onChange={(e) => setQuality(e.target.value as typeof quality)}
        >
          <option value="auto">Automatic (adaptive)</option>
          <option value="high">High</option>
          <option value="max">Maximum (no limits)</option>
        </select>
      </FieldRow>
      <FieldRow>
        <InputField
          id="bitrate"
          type="number"
          label="Max video bitrate (kbps, 0 = auto)"
          value={String(bitrate)}
          min={0}
          onChange={(e) => setBitrate(Number(e.target.value) || 0)}
        />
      </FieldRow>

      <h3 className={styles.sectionTitle}>Live Captions</h3>
      <FieldRow>
        <InputField
          id="liveCaptions"
          type="checkbox"
          label="Show live captions of your speech (Web Speech API)"
          checked={captions}
          onChange={(e) => setCaptions(e.target.checked)}
        />
      </FieldRow>

      <h3 className={styles.sectionTitle}>Call Timer</h3>
      <FieldRow>
        <InputField
          id="showTimer"
          type="checkbox"
          label="Show a live call-duration timer"
          checked={showTimer}
          onChange={(e) => setShowTimer(e.target.checked)}
        />
      </FieldRow>

      <h3 className={styles.sectionTitle}>Connection & Quality</h3>
      <FieldRow>
        <InputField
          id="connStats"
          type="checkbox"
          label="Show connection statistics overlay"
          checked={stats}
          onChange={(e) => setStats(e.target.checked)}
        />
      </FieldRow>
      <FieldRow>
        <label htmlFor="rtcMode">MatrixRTC mode</label>
        <select
          id="rtcMode"
          value={rtcMode}
          onChange={(e) => setRtcMode(e.target.value as typeof rtcMode)}
        >
          <option value="legacy">Legacy</option>
          <option value="compatibility">Compatibility</option>
          <option value="matrix_2_0">Nevai 2.0</option>
        </select>
      </FieldRow>
      <FieldRow>
        <InputField
          id="customLk"
          type="text"
          label="Custom LiveKit URL (leave empty for default)"
          value={lkUrl ?? ""}
          placeholder="wss://your-livekit.example.com"
          onChange={(e) => setLkUrl(e.target.value || null)}
        />
      </FieldRow>
    </div>
  );
};
